import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import Content from "./Content";
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  ColorLens as ColorLensIcon,
  Paid,
  PermMedia,
  SupportAgent,
  PriceCheck,
  AutoFixHigh,
  AutoAwesome,
  Unsubscribe,
  ContactMail,
  AddCard,
  ContentPaste,
  Language,
  Translate,
  TuneOutlined,
  SmartToyOutlined,
  StoreOutlined,
  PaletteOutlined,
  CloudSync,
  MonitorHeartOutlined,
  CampaignOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { UserProvider } from "../../context/UserContext";
import { AdminStudioProvider } from "../studio/AdminStudioContext";
import AdminStudioBar from "../studio/AdminStudioBar";
import AdminStudioContent from "../studio/AdminStudioContent";

const drawerWidth = 260;

const AdminDashboard = () => {
  const { lang } = React.useContext(TranslateContext);
  const [web, setWeb] = React.useState({});
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const { toggleColorMode, isDark } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  async function handleGetWeb() {
    const res = await hitAxios({
      path: "/api/web/get_web_public",
      post: false,
      admin: false,
    });
    if (res?.data?.success) setWeb(res.data.data);
  }

  // ── MENU GROUPS ────────────────────────────────────────────────────────────
  const menuItems = [
    // ── 1. Overview ──────────────────────────────────────────────────────────
    {
      id: "dashboards",
      text: lang?.dashboards || "Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: "generation-monitor",
      text: lang?.watchDogNav || lang?.generationMonitorNav || "WatchDog",
      icon: <MonitorHeartOutlined />,
    },

    // ── 2. Users ─────────────────────────────────────────────────────────────
    {
      id: "users",
      text: lang?.usersAccessTitle || lang?.users || "Users & Access",
      icon: <GroupIcon />,
    },

    // ── 3. AI & Automation ───────────────────────────────────────────────────
    {
      id: "ai-automation",
      text: lang?.aiAutomation || "AI & Automation",
      icon: <SmartToyOutlined />,
      submenu: [
        {
          id: "beyund-ai-provider",
          text: lang?.beyundAiProviders || "Beyund AI Providers",
        },
        {
          id: "model-registry",
          text: lang?.modelRegistry || "Model Registry",
        },
        {
          id: "prompt-template",
          text: lang?.promptTe || "Prompt Templates",
        },
        {
          id: "prompt-library",
          text: lang?.promptLibraryNav || "Prompt Library",
        },
        {
          id: "templates",
          text: lang?.templates || "Templates",
        },
        {
          id: "task-pricing",
          text: lang?.taskPricing || "Task Pricing",
        },
      ],
    },

    // ── 4. Monetization ──────────────────────────────────────────────────────
    {
      id: "monetization",
      text: lang?.monetization || "Monetization",
      icon: <StoreOutlined />,
      submenu: [
        {
          id: "plans",
          text: lang?.plans || "Plans",
        },
        {
          id: "payment-gateway",
          text: lang?.paymentGateway || "Payment Gateways",
        },
        {
          id: "orders",
          text: lang?.orders || "Orders",
        },
        {
          id: "poyo-profit",
          text: lang?.profitLossNav || "Profit/Loss",
        },
      ],
    },

    // ── 5. Marketing (growth) ─────────────────────────────────────────────────
    {
      id: "marketing",
      text: lang?.marketingNav || "Marketing",
      icon: <CampaignOutlined />,
      submenu: [
        {
          id: "marketing-overview",
          text: lang?.marketingOverview || "Overview",
        },
        {
          id: "email-campaigns",
          text: lang?.emailCampaigns || "Email Campaigns",
        },
        {
          id: "marketing-automations",
          text: lang?.marketingAutomations || "Automations",
        },
        {
          id: "marketing-audiences",
          text: lang?.marketingAudiences || "Audiences",
        },
        {
          id: "marketing-promos",
          text: lang?.marketingPromos || "Promo Codes",
        },
        {
          id: "affiliate",
          text: lang?.affiliateNav || "Affiliate",
        },
        {
          id: "leads",
          text: lang?.leads || "Leads",
        },
        {
          id: "lead-closer",
          text: lang?.leadCloserNav || "Beyund Assistant",
        },
      ],
    },

    // ── 6. Appearance ─────────────────────────────────────────────────────────
    {
      id: "appearance",
      text: lang?.appearance || "Appearance",
      icon: <PaletteOutlined />,
      submenu: [
        {
          id: "web-theme",
          text: lang?.webTheme || "Web Theme",
        },
        {
          id: "site-settings",
          text: lang?.siteSettings || "Site Settings",
        },
        {
          id: "ads-tracking",
          text: "Ads Tracking",
        },
        {
          id: "social-login",
          text: lang?.socialLogin || "Social Login",
        },
      ],
    },

    // ── 7. Content ────────────────────────────────────────────────────────────
    {
      id: "content",
      text: lang?.content || "Content",
      icon: <ContentPaste />,
      submenu: [
        {
          id: "blogs",
          text: lang?.blogs || "Blogs",
        },
      ],
    },

    // ── 8. Communications ─────────────────────────────────────────────────────
    {
      id: "communications",
      text: lang?.communications || "Communications",
      icon: <Unsubscribe />,
      submenu: [
        {
          id: "email-settings",
          text: lang?.emailSettings || "Email Settings",
        },
        {
          id: "email-templates",
          text: lang?.emailTemplates || "Email Templates",
        },
      ],
    },

    // ── 9. Localization ───────────────────────────────────────────────────────
    {
      id: "localization",
      text: lang?.localization || "Localization",
      icon: <Translate />,
      submenu: [
        {
          id: "web-translation",
          text: lang?.webTranslation || "Web Translation",
        },
      ],
    },

    // ── 10. Support ───────────────────────────────────────────────────────────
    {
      id: "help",
      text: lang?.help || "Help & Support",
      icon: <SupportAgent />,
    },

      {
      id: "update",
      text: lang?.updateApp || "Update App",
      icon: <CloudSync />,
    },
  ];

  // ── URL sync helpers ───────────────────────────────────────────────────────
  const getInitialMenu = () => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    if (pageParam === "user-profile") return pageParam;
    if (pageParam) {
      const isValid = menuItems.some(
        (item) =>
          item.id === pageParam ||
          item.submenu?.some((sub) => sub.id === pageParam),
      );
      if (isValid) return pageParam;
    }
    return menuItems[0]?.submenu?.[0]?.id || menuItems[0]?.id || "dashboards";
  };

  const [selectedMenu, setSelectedMenu] = React.useState(getInitialMenu);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState(() => {
    const initialMenu = getInitialMenu();
    const openMenus = {};
    menuItems.forEach((item) => {
      if (item.submenu?.some((sub) => sub.id === initialMenu)) {
        openMenus[item.id] = true;
      }
    });
    return openMenus;
  });

  const updateURL = (menuId) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", menuId);
    if (menuId !== "user-profile") params.delete("uid");
    window.history.pushState(
      { path: `${window.location.pathname}?${params}` },
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const handleMenuClick = (menuId, hasSubmenu) => {
    if (hasSubmenu) {
      setOpenSubmenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    } else {
      setSelectedMenu(menuId);
      updateURL(menuId);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleSubmenuClick = (submenuId) => {
    setSelectedMenu(submenuId);
    updateURL(submenuId);
    if (isMobile) setMobileOpen(false);
  };

  React.useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get("page");
      if (pageParam) {
        setSelectedMenu(pageParam);
        menuItems.forEach((item) => {
          if (item.submenu?.some((sub) => sub.id === pageParam)) {
            setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
          }
        });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  React.useEffect(() => {
    handleGetWeb();
  }, []);

  const isStudioPage = String(selectedMenu || "").startsWith("studio-");

  const studioPageLabels = {
    "studio-dashboard": lang?.studioUserDashboard || "User overview",
    "studio-influencers": lang?.influencers || "Characters",
    "studio-product-ads": lang?.productAdsNav || "Product Ads",
    "studio-music": lang?.musicNav || "Music",
    "studio-billing": lang?.billingNav || "Billing",
    "studio-usage": lang?.usage || "Usage",
    "studio-notifications": lang?.notification || "Notifications",
    "studio-user-help": lang?.studioUserHelp || "User support",
    "studio-gallery": lang?.gallery || "Gallery",
    "studio-content": lang?.contentVideos || "Content videos",
    "studio-actors": lang?.actorsNav || "Actors",
    "studio-voices": lang?.voicesNav || "Voices",
    "studio-ugc-workflow": lang?.ugcAdsWorkflow || "Ads workflow",
    "studio-video-agent": lang?.videoAgentNav || "Video agent",
    "studio-makeugc": lang?.makeUgcNav || "MakeUGC",
    "studio-script-to-video": lang?.scriptToVideo || "Script to video",
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Sidebar
        lang={lang}
        theme={theme}
        isMobile={isMobile}
        selectedMenu={selectedMenu === "user-profile" ? "users" : selectedMenu}
        openSubmenus={openSubmenus}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        handleMenuClick={handleMenuClick}
        handleSubmenuClick={handleSubmenuClick}
        menuItems={menuItems}
        web={web}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 0 },
        }}
      >
        <TopBar
          lang={lang}
          theme={theme}
          isMobile={isMobile}
          isDark={isDark}
          selectedMenu={selectedMenu}
          pageLabelOverride={
            selectedMenu === "user-profile"
              ? lang?.userProfileTitle || "User Profile"
              : isStudioPage
                ? studioPageLabels[selectedMenu]
                : undefined
          }
          toggleColorMode={toggleColorMode}
          handleDrawerToggle={handleDrawerToggle}
          web={web}
        />

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {isStudioPage ? (
            <AdminStudioProvider>
              <AdminStudioBar />
              <UserProvider studio>
                <AdminStudioContent selectedMenu={selectedMenu} />
              </UserProvider>
            </AdminStudioProvider>
          ) : (
            <Content
              selectedMenu={selectedMenu}
              web={web}
              onNavigate={handleSubmenuClick}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
